import { useEffect, useState } from "react";
import { Timestamp } from "firebase/firestore";
import AdminButton from "../../../core/admin/adminButton";
import AdminToast from "../../../core/admin/adminToast";
import AdminPage from "../../../core/admin/adminPage";
import AdminCard from "../../../core/admin/adminCard";
import AdminTable from "../../../core/admin/adminTable";
import AdminBadge, { AdminBadgeTone } from "../../../core/admin/adminBadge";
import AdminCallout from "../../../core/admin/adminCallout";
import AdminEmptyState from "../../../core/admin/adminEmptyState";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import { FileTextIcon, RefreshIcon } from "../../../../img/svg/adminIcons";
import { getAdminLogs, IAdminLog } from "../../../../utils/adminLog";
import { ADMIN_LOGGED_ACTIONS, adminLogActionFields } from "../../../../utils/fields";

const PAGE_SIZE = 25;

function formatMoment(time: Timestamp | null) {
    if (!time?.seconds) {
        // serverTimestamp() só resolve no servidor: um registro recém-criado
        // pode chegar aqui sem data até o próximo carregamento
        return "—";
    }
    const date = new Date(time.seconds * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()} ${hours}:${minutes}`;
}

// concessões em verde, remoções em vermelho, validações manuais em azul
function actionTone(action: string): AdminBadgeTone {
    if (action.endsWith(".grant") || action === "user.create") {
        return "success";
    }
    if (action.endsWith(".revoke")) {
        return "danger";
    }
    if (action.startsWith("verify.")) {
        return "info";
    }
    return "neutral";
}

function Person({ name, email }: { name: string | null, email: string | null }) {
    return (
        <div className="min-w-0">
            <p className="font-semibold text-gray-900 break-words">{name || "—"}</p>
            {
                email ?
                    <p className="text-xs text-gray-500 break-all">{email}</p>
                    : null
            }
        </div>
    )
}

export default function AdminLogs() {

    const [logs, setLogs] = useState<IAdminLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [badToast, setBadToast] = useState(false);

    const loadLogs = async (after?: Timestamp | null) => {
        setLoading(true);
        const result = await getAdminLogs(PAGE_SIZE, after);
        if (!result) {
            setLoading(false);
            setLoaded(true);
            setBadToast(true);
            return null;
        }
        setLogs(previous => after ? [...previous, ...result] : result);
        setHasMore(result.length === PAGE_SIZE);
        setLoading(false);
        setLoaded(true);
    }

    useEffect(() => {
        loadLogs();
        // eslint-disable-next-line
    }, []);

    const loadMore = () => {
        const last = logs[logs.length - 1];
        loadLogs(last?.createdAt);
    }

    return (
        <AdminPage
            title="Logs do sistema"
            description="Histórico de alterações feitas pelo painel, do mais recente para o mais antigo."
            actions={<AdminButton variant="secondary" icon={<RefreshIcon width="16px" />} disabled={loading} onClick={() => loadLogs()}>Atualizar</AdminButton>}
        >
            <AdminCallout tone="gold" title="O que é registrado hoje">
                <p>Apenas as ações de <strong>gestão de usuário</strong> (aba Usuários):</p>
                <ul className="list-disc list-inside mt-1">
                    {
                        ADMIN_LOGGED_ACTIONS.map((action, idx) => (
                            <li key={idx}>{action}</li>
                        ))
                    }
                </ul>
                <p className="mt-2">
                    As demais áreas do painel (relógios, anúncios, banners, FAQs, avaliações, home)
                    <strong> ainda não geram log</strong>. Registros não podem ser editados nem apagados pelo painel.
                </p>
            </AdminCallout>

            <AdminCard title="Histórico de alterações" bodyClassName="p-3 md:p-4">
                {
                    logs.length ?
                        <AdminTable
                            rows={logs}
                            rowKey={log => log.id}
                            columns={[
                                { header: "Ação", mobile: "title", render: log => <AdminBadge tone={actionTone(log.action)}>{adminLogActionFields[log.action] || log.action}</AdminBadge> },
                                { header: "Quando", mobile: "aside", className: "whitespace-nowrap", render: log => <span className="text-sm text-gray-600 tabular-nums">{formatMoment(log.createdAt)}</span> },
                                { header: "Feito por", render: log => <Person name={log.actorName} email={log.actorEmail} /> },
                                { header: "Usuário afetado", render: log => <Person name={log.targetName} email={log.targetEmail} /> },
                                { header: "Detalhes", className: "max-w-xs", render: log => <span className="text-xs text-gray-600 break-words">{log.details || "—"}</span> },
                            ]}
                        />
                        : loaded && !loading ?
                            <AdminEmptyState icon={<FileTextIcon />} title="Nenhum registro por enquanto." />
                            : null
                }

                {
                    loading ?
                        <AdminLoading />
                        : null
                }

                {
                    hasMore && !loading ?
                        <div className="flex justify-center mt-4">
                            <AdminButton variant="secondary" onClick={loadMore}>Carregar mais</AdminButton>
                        </div>
                        : null
                }
            </AdminCard>
            <AdminToast message="Erro ao carregar os logs!" toast={badToast} setToast={setBadToast} tone="danger" />
        </AdminPage>
    )
}
